// ─── Section 105 Plan Advisor — Seed Data ───────────────────────
// Defines the 6 questions, the 5 plan types, their specs, rules, deliverables,
// and default document templates. Administered via Admin → IBOS Layers → 105 Advisor tab.

export const SEED_105_QUESTIONS = [
  {
    id: 'size',
    internalWording: 'How many full-time equivalent employees?',
    employerWording: 'How many full-time employees does your company have?',
    internalHelp: 'Determines ACA obligations and which HRA types are available. Count all employees averaging 30+ hours/week.',
    employerHelp: 'Count employees who work 30 or more hours per week on average. This helps us determine which plan types are available to you.',
    options: [
      { id: 'under50', label: 'Under 50 FTEs', sublabel: 'Small employer — not subject to ACA employer mandate' },
      { id: '50plus', label: '50 or more FTEs', sublabel: 'Applicable Large Employer — subject to ACA mandate' },
    ],
  },
  {
    id: 'groupHealth',
    internalWording: 'Does the employer offer group health insurance?',
    employerWording: 'Does your company currently offer a group health insurance plan?',
    internalHelp: 'Most important factor in determining plan type. Group health means a traditional employer-sponsored plan.',
    employerHelp: 'This means a health insurance plan your company pays for or contributes to — like Blue Cross, United Healthcare, or Aetna offered to employees.',
    options: [
      { id: 'yes', label: 'Yes — offers group health insurance', sublabel: 'Employer sponsors a group health plan (BCBS, UHC, Aetna, etc.)' },
      { id: 'no', label: 'No — does not offer group health', sublabel: 'No employer-sponsored group health plan' },
      { id: 'considering', label: 'Considering dropping group health', sublabel: 'Currently offers but may want to switch to an HRA' },
    ],
  },
  {
    id: 'goal',
    internalWording: 'What is the primary goal?',
    employerWording: 'What would you most like to accomplish?',
    internalHelp: 'Different 105 structures optimize for different outcomes.',
    employerHelp: "There's no wrong answer — this helps us find the best fit for your situation.",
    options: [
      { id: 'supplement', label: 'Supplement existing benefits', sublabel: 'Help employees with out-of-pocket costs alongside existing coverage' },
      { id: 'replace', label: 'Replace group health with HRA', sublabel: 'Fund individual health insurance instead of a group plan' },
      { id: 'taxfree', label: 'Provide tax-free healthcare funds', sublabel: 'Give employees tax-advantaged money for medical expenses' },
      { id: 'combine125', label: 'Combine with Section 125 optimization', sublabel: 'Maximize combined tax savings with the HealthCues program' },
    ],
  },
  {
    id: 'budget',
    internalWording: 'What monthly budget per employee?',
    employerWording: 'How much could you contribute per employee per month?',
    internalHelp: 'Determines plan structure and regulatory caps.',
    employerHelp: 'This is an employer contribution — it does not come out of employee paychecks. It is a tax-deductible business expense for you.',
    options: [
      { id: 'low', label: 'Up to $100/month', sublabel: 'Conservative — basic expense coverage', amount: 100 },
      { id: 'mid', label: '$100 — $300/month', sublabel: 'Moderate — solid coverage for most expenses', amount: 200 },
      { id: 'high', label: '$300 — $600/month', sublabel: 'Comprehensive — could fund individual premiums', amount: 450 },
      { id: 'premium', label: '$600+/month', sublabel: 'Full replacement — individual premium reimbursement', amount: 700 },
    ],
  },
  {
    id: 'classes',
    internalWording: 'Should all employees receive the same benefit?',
    employerWording: 'Would you like to offer different amounts to different groups?',
    internalHelp: 'Some HRA types allow different contribution levels by permitted employee classes.',
    employerHelp: 'For example, different amounts for full-time versus part-time, or more for employees with families.',
    options: [
      { id: 'same', label: 'Same for everyone', sublabel: 'All eligible employees get the same monthly amount' },
      { id: 'byClass', label: 'Different by employee class', sublabel: 'Different amounts for different groups (management vs hourly, etc.)' },
      { id: 'byAgeFamily', label: 'Varies by age or family size', sublabel: 'Older employees or those with families get more' },
    ],
  },
  {
    id: 'timeline',
    internalWording: 'When does the employer want to start?',
    employerWording: 'When would you like to get started?',
    internalHelp: '',
    employerHelp: '',
    options: [
      { id: 'asap', label: 'As soon as possible', sublabel: 'Within 30 days' },
      { id: 'quarter', label: 'Next quarter', sublabel: '60-90 days — standard implementation' },
      { id: 'nextYear', label: 'Next plan year (January 1)', sublabel: 'Align with benefits renewal cycle' },
    ],
  },
];

export const SEED_105_PLAN_TYPES = [
  {
    id: 'qsehra',
    type: 'QSEHRA',
    fullName: 'Qualified Small Employer Health Reimbursement Arrangement',
    tagline: 'Best fit for small employers without group health',
    eligibility: 'Employers with under 50 FTEs who do NOT offer group health insurance',
    description: 'QSEHRA lets you reimburse employees tax-free for individual health insurance premiums and qualified medical expenses. Simple administration, IRS-set annual contribution caps, and minimal compliance burden.',
    descriptionEmployer: 'A simple plan for smaller companies without group insurance. You reimburse employees tax-free for their health insurance premiums and out-of-pocket medical costs. Low-complexity setup and minimal paperwork.',
    status: 'active',
    specs: {
      contributionCaps: '$6,150/individual, $12,450/family (2026 est.)',
      complexity: 'Low',
      setupTime: '2-4 weeks',
      erisa: 'Exempt from most ERISA requirements',
      acaImpact: 'Not subject to employer mandate (under 50)',
      nonDiscrimination: 'Must be offered on same terms to all eligible employees',
      requiresMEC: 'Employees must have MEC to receive tax-free reimbursements',
    },
    keyRules: [
      'Cannot be offered alongside group health insurance',
      'Same terms for all eligible employees (no class variation)',
      'Must provide 90-day advance written notice to employees',
      'Unused amounts can carry over (at employer option)',
      'Reimbursements reported on employee W-2 (Box 12, Code FF)',
      'Employee must provide proof of MEC coverage',
    ],
    talkingPointsInternal: [
      'Simple admin — IRS sets the caps, no discrimination testing',
      'Works best when employer is under 50 FTEs and has NO group health',
      'Employee must carry MEC (ACA plan, spouse plan, etc.) to receive tax-free reimbursements',
      'Carryover is optional — employer decides',
    ],
    talkingPointsEmployer: [
      'Reimburse employees tax-free for their health insurance and medical costs',
      'Much simpler than running a group health plan',
      'No headcount growth penalty — designed specifically for small businesses',
      'Employees pick the insurance plan that works for them',
    ],
    deliverables: [
      { id: 'qsehra_plan_doc', title: 'Plan Document', description: 'Legal plan terms and reimbursement procedures' },
      { id: 'qsehra_spd', title: 'Summary Plan Description', description: 'Employee-facing plain-language summary' },
      { id: 'qsehra_notice', title: '90-Day Employee Notice', description: 'Required pre-plan-year notice template' },
      { id: 'qsehra_claim', title: 'Reimbursement Claim Form', description: 'Expense submission template' },
      { id: 'qsehra_w2_guide', title: 'W-2 Reporting Guide', description: 'Box 12 Code FF reporting walkthrough' },
      { id: 'qsehra_checklist', title: 'Compliance Checklist', description: 'Regulatory requirements and deadlines' },
      { id: 'qsehra_talking', title: 'Talking Points', description: 'Key messages for client conversation' },
    ],
  },
  {
    id: 'excepted_benefit',
    type: 'Excepted Benefit HRA',
    fullName: 'Excepted Benefit Health Reimbursement Arrangement',
    tagline: 'Best for supplementing existing group health at low cost',
    eligibility: 'Any employer that offers group health insurance',
    description: 'A simple, low-cap HRA that supplements existing group health insurance. Covers expenses not paid by the group plan — deductibles, copays, dental, vision, Rx. Employees don\u2019t need to be enrolled in the group plan to participate.',
    descriptionEmployer: 'A lightweight HRA that tops up your existing group health plan with tax-free funds for deductibles, copays, dental, vision, and prescriptions. Low annual cap but very easy to manage.',
    status: 'active',
    specs: {
      contributionCaps: '$2,150/year per employee (2026 est.)',
      complexity: 'Very low',
      setupTime: '1-2 weeks',
      erisa: 'Excepted benefit — lighter ERISA requirements',
      acaImpact: 'No ACA impact — excepted benefit status',
      nonDiscrimination: 'Same terms for all eligible employees',
      requiresMEC: 'No — employee does NOT need to be enrolled in group plan',
    },
    keyRules: [
      'Must be offered in conjunction with group health insurance (employer offers it, employee doesn\u2019t have to enroll)',
      'Fixed annual cap set by IRS — cannot exceed limit',
      'Same terms for all eligible employees',
      'Can cover: deductibles, copays, coinsurance, dental, vision, Rx, OTC',
      'Cannot cover individual health insurance premiums',
      'Unused amounts can carry over (at employer option)',
    ],
    talkingPointsInternal: [
      'Ideal ADD-ON for employers who already have group health',
      'Low cap but minimal compliance burden',
      'Great fit to combine with Section 125 at low employer cost',
    ],
    talkingPointsEmployer: [
      'Tops up your existing group health plan with tax-free money for out-of-pocket costs',
      'Covers copays, deductibles, dental, vision, and prescriptions',
      'Simple to administer — very lightweight',
    ],
    deliverables: [
      { id: 'eb_plan_doc', title: 'Plan Document', description: 'Legal plan terms' },
      { id: 'eb_spd', title: 'Summary Plan Description', description: 'Employee summary' },
      { id: 'eb_claim', title: 'Reimbursement Claim Form', description: 'Expense submission' },
      { id: 'eb_integration_guide', title: 'Integration Guide', description: 'How it works alongside group health' },
      { id: 'eb_125_calculator', title: 'Combined 125+HRA Calculator', description: 'Savings layering analysis' },
      { id: 'eb_checklist', title: 'Compliance Checklist', description: 'Regulatory requirements' },
    ],
  },
  {
    id: 'ichra',
    type: 'ICHRA',
    fullName: 'Individual Coverage Health Reimbursement Arrangement',
    tagline: 'Best for large employers replacing group health',
    eligibility: 'Any employer — most commonly used by ALEs (50+) as group health alternative',
    description: 'ICHRA lets you fund employees\u2019 individual health insurance purchases with no cap on contributions. You can vary amounts by up to 11 employee classes. When properly structured, it satisfies the ACA employer mandate for ALEs.',
    descriptionEmployer: 'A flexible plan that replaces group health insurance. You give employees a monthly amount to buy their own individual health plans. No cap, and you can offer different amounts to different employee groups.',
    status: 'active',
    specs: {
      contributionCaps: 'No IRS cap — employer sets the amount',
      complexity: 'Moderate to high',
      setupTime: '4-8 weeks',
      erisa: 'Subject to ERISA — plan document and fiduciary duties required',
      acaImpact: 'Can satisfy ACA employer mandate with affordability safe harbor',
      nonDiscrimination: 'Can vary by 11 permitted employee classes',
      requiresMEC: 'Employee must enroll in individual health insurance (not just MEC)',
    },
    keyRules: [
      'Cannot be offered to the same class of employees who have access to group health — must choose one or the other per class',
      '11 permitted employee classes: FT/PT, salaried/hourly, geographic, seasonal, CBA, etc.',
      'Employee must have individual health insurance to receive reimbursements',
      'Must provide 90-day advance written notice with opt-out rights',
      'ACA affordability: lowest-cost Silver plan minus ICHRA amount must be \u2264 9.12% of employee income',
      'Requires Form 1095-C reporting with ICHRA-specific codes',
    ],
    talkingPointsInternal: [
      'Primary alternative to traditional group health for ALEs',
      'Affordability safe harbor must be calculated to avoid ACA penalties',
      'Can vary by 11 classes — design carefully',
      'Transition from group health is a 4-8 week project',
    ],
    talkingPointsEmployer: [
      'You give each employee a set monthly amount to buy their own health insurance',
      'Flexibility to offer different amounts to different employee groups',
      'No cap — you decide the budget',
      'Meets your ACA obligations when structured correctly',
    ],
    deliverables: [
      { id: 'ichra_plan_doc', title: 'Plan Document', description: 'Legal plan terms' },
      { id: 'ichra_spd', title: 'Summary Plan Description', description: 'Employee summary' },
      { id: 'ichra_class_design', title: 'Employee Class Design', description: 'Based on census data' },
      { id: 'ichra_aca_safe_harbor', title: 'ACA Affordability Analysis', description: 'Safe harbor calculation' },
      { id: 'ichra_1095c', title: 'Form 1095-C Reporting Guide', description: 'ICHRA-specific codes' },
      { id: 'ichra_transition', title: 'Group-to-ICHRA Transition Plan', description: 'Timeline and communications' },
      { id: 'ichra_exchange_notice', title: 'Exchange Notice Template', description: 'Employee marketplace guidance' },
      { id: 'ichra_checklist', title: 'Compliance Checklist', description: 'Regulatory requirements' },
    ],
  },
  {
    id: 'integrated_merp',
    type: 'Integrated HRA',
    fullName: 'Section 105 Medical Expense Reimbursement Plan',
    tagline: 'Best for combining with Section 125 optimization',
    eligibility: 'Any employer — ideal when primary goal is maximizing combined 125 + 105 value',
    description: 'A traditional Section 105 MERP provides maximum flexibility to combine with the HealthCues Section 125 program. The employer contributes a fixed monthly amount, tax-deductible for the employer and tax-free for the employee. This bridges the eligibility gap for lower-income employees and adds tangible healthcare value.',
    descriptionEmployer: 'A flexible plan that works alongside your Section 125 program to deliver maximum combined value. You contribute a fixed monthly amount; employees get tax-free reimbursement for medical expenses. Bridges the eligibility gap so more employees benefit.',
    status: 'active',
    specs: {
      contributionCaps: 'No IRS cap — employer sets the amount',
      complexity: 'Low to moderate',
      setupTime: '2-4 weeks',
      erisa: 'Subject to ERISA — plan document required',
      acaImpact: 'Does not satisfy ACA mandate alone — pair with MEC or group health',
      nonDiscrimination: 'Section 105(h) non-discrimination rules apply',
      requiresMEC: 'No — but does not count as MEC',
    },
    keyRules: [
      'Employer contributions are tax-deductible business expenses',
      'Employee reimbursements are tax-free (no income tax, no FICA)',
      'Must pass Section 105(h) non-discrimination testing',
      'Can cover qualified medical expenses: deductibles, Rx, preventive care, telehealth',
      'Can be offered alongside group health or standalone',
      'When combined with Section 125: employees get pre-tax premium AND tax-free reimbursement',
    ],
    talkingPointsInternal: [
      'Best fit when primary goal is combining 105 with the HealthCues 125 program',
      'Bridges eligibility gap for lower-salary employees — recalc FICA savings',
      'Section 105(h) non-discrimination testing required',
      'Does not count as MEC — pair with MEC or group health if ACA relevant',
    ],
    talkingPointsEmployer: [
      'Tax-deductible for your business, tax-free for your employees',
      'Bridges the gap: qualifies more employees for the HealthCues program',
      'Combined with Section 125: double-layer tax efficiency',
      'Our optimizer calculates the exact combined savings per employee',
    ],
    deliverables: [
      { id: 'merp_plan_doc', title: 'Plan Document', description: 'Legal plan terms with MERP articles' },
      { id: 'merp_spd', title: 'Summary Plan Description', description: 'Employee summary' },
      { id: 'merp_claim', title: 'Reimbursement Claim Form', description: 'Expense submission' },
      { id: 'merp_combined_analysis', title: '125+105 Combined Analysis', description: 'Per-employee optimizer output' },
      { id: 'merp_bridge_report', title: 'Eligibility Bridge Report', description: 'Who moves from ineligible to eligible' },
      { id: 'merp_105h_worksheet', title: 'Section 105(h) Test Worksheet', description: 'Non-discrimination testing' },
      { id: 'merp_sensitivity', title: 'HRA Sensitivity Analysis', description: 'Optimal contribution level' },
      { id: 'merp_checklist', title: 'Compliance Checklist', description: 'Regulatory requirements' },
      { id: 'merp_deck', title: 'Client Presentation', description: 'Slides for the client meeting' },
    ],
  },
  {
    id: 'gchra',
    type: 'GCHRA',
    fullName: 'Group Coverage Health Reimbursement Arrangement',
    tagline: 'Supplements group health with higher limits than Excepted Benefit',
    eligibility: 'Employers that offer group health insurance and want higher contribution limits',
    description: 'A GCHRA supplements the employer\u2019s existing group health plan with no IRS cap on contributions. Unlike the Excepted Benefit HRA ($2,150 limit), a GCHRA can reimburse any amount. However, employees must be enrolled in the group health plan to participate.',
    descriptionEmployer: 'A supplemental HRA on top of your group health plan with no cap on your contributions. Employees enrolled in your group plan get tax-free reimbursement for expenses the group plan doesn\u2019t cover.',
    status: 'active',
    specs: {
      contributionCaps: 'No IRS cap — employer sets the amount',
      complexity: 'Moderate',
      setupTime: '2-4 weeks',
      erisa: 'Subject to ERISA',
      acaImpact: 'Integrates with existing group health — no separate ACA considerations',
      nonDiscrimination: 'Section 105(h) non-discrimination rules apply',
      requiresMEC: 'Employee must be enrolled in the employer\u2019s group health plan',
    },
    keyRules: [
      'Employee MUST be enrolled in the group health plan to participate',
      'No cap on employer contributions',
      'Covers expenses not covered by the group plan',
      'Subject to Section 105(h) non-discrimination testing',
      'Can vary contributions by employee class if non-discrimination tests pass',
      'Unused amounts can carry over',
    ],
    talkingPointsInternal: [
      'Use when employer wants higher HRA cap than Excepted Benefit allows',
      'Requires group health enrollment — limits eligibility vs Excepted Benefit',
      'Subject to 105(h) non-discrimination testing',
    ],
    talkingPointsEmployer: [
      'Tops up your group health plan with higher tax-free reimbursement limits',
      'No IRS cap — you set the amount',
      'Employees get reimbursed for deductibles, copays, and more',
    ],
    deliverables: [
      { id: 'gc_plan_doc', title: 'Plan Document', description: 'Legal plan terms' },
      { id: 'gc_spd', title: 'Summary Plan Description', description: 'Employee summary' },
      { id: 'gc_claim', title: 'Reimbursement Claim Form', description: 'Expense submission' },
      { id: 'gc_105h_worksheet', title: 'Section 105(h) Test Worksheet', description: 'Non-discrimination testing' },
      { id: 'gc_integration_guide', title: 'Integration Guide', description: 'How it pairs with group health' },
      { id: 'gc_checklist', title: 'Compliance Checklist', description: 'Regulatory requirements' },
    ],
  },
];

export const SEED_105_IRS_LIMITS = {
  qsehra_individual_cap: 6150,
  qsehra_family_cap: 12450,
  excepted_benefit_cap: 2150,
  aca_affordability_pct: 9.12,
  aca_penalty_a: 2970,
  year: 2026,
};

// ─── Coverage Layer Defaults (MEC + Health Share) ───────────────
// These are admin-editable in IBOS Admin. Cost estimates feed the package
// math. Health Share disclaimers are compliance-locked.
export const SEED_105_COVERAGE_LAYERS = {
  mec: {
    id: 'mec',
    name: 'MEC Plan',
    fullName: 'Minimum Essential Coverage Plan',
    monthlyCostEstimate: 50, // per employee
    monthlyCostRange: [40, 60],
    provider: '',
    status: 'available',
    icon: 'shield-checkmark-outline',
    employerSummary: 'Low-cost preventive coverage that satisfies ACA minimum essential coverage requirements.',
    employeeSummary: 'Preventive care services, wellness exams, screenings, and immunizations — the foundation of healthy benefits.',
    benefits: [
      'Preventive care services (no copay)',
      'Wellness exams and annual screenings',
      'Immunizations and vaccinations',
      'ACA-compliant minimum coverage',
    ],
    rolesByPackage: {
      qsehra_mec: 'Required — employees must have MEC for tax-free QSEHRA reimbursements. Without it, QSEHRA reimbursements become taxable.',
      integrated_merp_mec: 'Recommended for ALEs (50+ FTEs) — avoids the $2,970/employee ACA §4980H(a) penalty. Optional for small employers.',
      ichra: 'Not separately required — ICHRA requires individual health insurance which inherently provides MEC.',
    },
  },
  health_share: {
    id: 'health_share',
    name: 'Health Share',
    fullName: 'Health Share Ministry',
    monthlyCostEstimate: 100,
    monthlyCostRange: [75, 150],
    provider: '',
    status: 'available',
    icon: 'people-outline',
    employerSummary: 'Community-based healthcare cost-sharing model. Lower cost than traditional insurance but NOT classified as MEC under the ACA.',
    employeeSummary: 'Community-based medical cost sharing with telemedicine, preventive care, and prescription discounts.',
    benefits: [
      'Community-based medical cost sharing',
      'Telemedicine integration',
      'Preventive care incentives',
      'Discount Rx access',
      'Wellness engagement',
    ],
    // Compliance-locked — these cannot be removed via admin UI
    requiredDisclaimers: [
      'Health Shares are NOT insurance',
      'Health Shares are NOT Minimum Essential Coverage under the ACA',
      'QSEHRA reimbursements may be TAXABLE if the employee\u2019s only coverage is a Health Share',
      'ICHRA CANNOT reimburse Health Share premiums',
      'Must be marketed carefully per state regulations',
      'Employees may need separate MEC-qualifying coverage for tax-free treatment',
      'Legal review required before implementation',
    ],
    rolesByPackage: {
      qsehra_healthshare: 'Lower-cost alternative to MEC. ⚠ Health Share alone does NOT satisfy QSEHRA\u2019s MEC requirement — employees may need separate MEC.',
      integrated_merp_healthshare: 'Innovative preventive-first option — pairs well with MERP for forward-thinking employers.',
    },
  },
};

// ─── Section 105 Package Definitions ────────────────────────────
// Each package combines a 105 plan + coverage layer (or none) + Section 125.
// Packages are admin-editable in IBOS Admin → 105 Advisor → Packages tab.
export const SEED_105_PACKAGES = [
  {
    id: 'qsehra_mec',
    name: 'QSEHRA + MEC',
    section105Type: 'qsehra',
    coverageLayer: 'mec',
    section125: true,
    headlineInternal: 'Tax-free reimbursement with required preventive coverage',
    headlineEmployer: 'Healthcare reimbursement + preventive coverage',
    whyThisPackage: 'As a small employer without group health, QSEHRA lets you reimburse employees tax-free. Employees MUST have Minimum Essential Coverage to receive tax-free reimbursements — so a low-cost MEC plan is included to satisfy this requirement.',
    whyThisPackageEmployer: 'A simple plan that gives employees tax-free funds for healthcare expenses, plus preventive care coverage they can rely on.',
    components: [
      { layer: 'Section 125', role: 'Pre-tax premium deduction — generates FICA savings', status: 'active', costToEmployer: 'Zero', employeeImpact: 'Take-home pay increase' },
      { layer: 'QSEHRA', role: 'Tax-free reimbursement for medical expenses', status: 'recommended', costToEmployer: 'Up to $6,150/yr individual', employeeImpact: 'Tax-free healthcare funds' },
      { layer: 'MEC Plan', role: 'Required — enables tax-free QSEHRA reimbursements', status: 'required', costToEmployer: '$40-60/employee/month', employeeImpact: 'Preventive care coverage + ACA compliance' },
    ],
    estMonthlyEmployerCost: 150,
    employerCostRange: [140, 160],
    bestFor: 'Small employers (under 50 FTEs) without group health',
    bestForEmployer: 'Small companies without a group health plan',
    pros: ['Simple, safe, IRS-compliant', 'Tax-free for employees', 'Preventive care included'],
    cons: ['Annual cap (IRS-set)', 'Same amount for all eligible employees'],
    complianceNotes: [
      'QSEHRA requires employees to have MEC — the MEC plan satisfies this',
      'MEC provides ACA-compliant preventive coverage',
      'QSEHRA reimbursements without MEC become taxable income',
    ],
    setupWeeks: '2-4 weeks',
    legalReview: 'Standard',
  },
  {
    id: 'qsehra_healthshare',
    name: 'QSEHRA + Health Share',
    section105Type: 'qsehra',
    coverageLayer: 'health_share',
    section125: true,
    headlineInternal: 'Lower-cost alternative — community-based healthcare',
    headlineEmployer: 'A lower-cost option with broader care',
    whyThisPackage: 'Same QSEHRA structure as Package 1, but with a Health Share ministry instead of MEC. Lower monthly cost and broader care options. Compliance complexity higher — Health Shares are NOT MEC, so reimbursements may be taxable for some employees.',
    whyThisPackageEmployer: 'Get the benefits of QSEHRA with a community-based healthcare option that costs less than traditional MEC.',
    components: [
      { layer: 'Section 125', role: 'Pre-tax premium deduction — generates FICA savings', status: 'active', costToEmployer: 'Zero', employeeImpact: 'Take-home pay increase' },
      { layer: 'QSEHRA', role: 'Tax-free reimbursement (subject to MEC requirement)', status: 'recommended', costToEmployer: 'Up to $6,150/yr individual', employeeImpact: 'Tax-free healthcare funds (if MEC is met)' },
      { layer: 'Health Share', role: 'Alternative coverage — NOT MEC', status: 'alternative', costToEmployer: '$75-150/employee/month', employeeImpact: 'Community-based care + telemedicine + Rx discounts' },
    ],
    estMonthlyEmployerCost: 125,
    employerCostRange: [100, 175],
    bestFor: 'Small employers (under 50 FTEs), cost-conscious, willing to navigate compliance',
    bestForEmployer: 'Cost-conscious small companies',
    pros: ['Lower employer cost than MEC option', 'Broader care via community sharing'],
    cons: ['Health Share ≠ MEC — compliance risk', 'Reimbursements may be taxable', 'Legal review required'],
    complianceNotes: [
      'Health Share ministries are NOT classified as Minimum Essential Coverage under the ACA',
      'Employees using only a Health Share may not receive tax-free QSEHRA reimbursements',
      'Employees should obtain MEC-qualifying coverage separately if tax-free treatment is desired',
      'Consult benefits counsel before implementation',
    ],
    setupWeeks: '4-6 weeks',
    legalReview: 'Required',
    flagged: true,
  },
  {
    id: 'ichra',
    name: 'ICHRA (Self-Contained)',
    section105Type: 'ichra',
    coverageLayer: null,
    section125: true,
    headlineInternal: 'Replace group health with individual coverage funding',
    headlineEmployer: 'Flexible plan — employees pick their own coverage',
    whyThisPackage: 'ICHRA is self-contained: the required individual health insurance IS the coverage layer. No separate MEC needed. Best for ALEs replacing group health or small employers with higher budgets seeking 11-class flexibility.',
    whyThisPackageEmployer: 'Give each employee a set monthly amount to buy their own health insurance. Maximum flexibility — vary amounts by employee group.',
    components: [
      { layer: 'Section 125', role: 'Pre-tax premium deduction — generates FICA savings', status: 'active', costToEmployer: 'Zero', employeeImpact: 'Take-home pay increase' },
      { layer: 'ICHRA', role: 'Reimburses individual health insurance premiums', status: 'recommended', costToEmployer: 'Employer-set, no cap', employeeImpact: 'Funds individual insurance + medical expenses' },
    ],
    estMonthlyEmployerCost: 450,
    employerCostRange: [350, 600],
    bestFor: '50+ FTEs replacing group health, or smaller employers with higher budgets',
    bestForEmployer: 'Companies replacing group health, or with higher budgets',
    pros: ['No IRS cap on contributions', '11-class flexibility', 'Self-contained — no separate MEC'],
    cons: ['ACA affordability safe harbor must be calculated', 'Higher complexity', 'Health Share NOT reimbursable'],
    complianceNotes: [
      'Employees must have individual health insurance (which inherently provides MEC)',
      'ICHRA CANNOT reimburse Health Share premiums',
      'ACA affordability safe harbor required for ALEs',
      'Form 1095-C reporting with ICHRA-specific codes',
    ],
    setupWeeks: '4-8 weeks',
    legalReview: 'Standard',
  },
  {
    id: 'excepted_benefit',
    name: 'Excepted Benefit HRA',
    section105Type: 'excepted_benefit',
    coverageLayer: null, // group health already in place
    section125: true,
    headlineInternal: 'Lightweight supplement — group health already in place',
    headlineEmployer: 'A simple top-up for your existing health plan',
    whyThisPackage: 'No separate coverage layer needed — the employer\u2019s existing group health plan provides all necessary coverage. The Excepted Benefit HRA just supplements it with a low-cap, low-complexity tax-free reimbursement for copays/deductibles/dental/vision/Rx.',
    whyThisPackageEmployer: 'Add tax-free funds for out-of-pocket costs on top of your existing group health plan. Easy to administer.',
    components: [
      { layer: 'Section 125', role: 'Pre-tax premium deduction — generates FICA savings', status: 'active', costToEmployer: 'Zero', employeeImpact: 'Take-home pay increase' },
      { layer: 'Excepted Benefit HRA', role: 'Tax-free reimbursement up to IRS cap', status: 'recommended', costToEmployer: 'Up to $2,150/yr per employee', employeeImpact: 'Tax-free funds for out-of-pocket costs' },
      { layer: 'Existing Group Health', role: 'Already in place — provides primary coverage', status: 'in_place', costToEmployer: 'Existing', employeeImpact: 'Existing coverage' },
    ],
    estMonthlyEmployerCost: 80,
    employerCostRange: [60, 100],
    bestFor: 'Employers who already offer group health, modest supplement',
    bestForEmployer: 'Companies with an existing group health plan',
    pros: ['Very low complexity', 'Excepted from most ERISA rules', 'No additional coverage layer needed'],
    cons: ['Annual cap of $2,150', 'Cannot fund insurance premiums'],
    complianceNotes: [
      'Must be offered alongside group health insurance',
      'Same terms for all eligible employees',
      'Cannot reimburse individual health insurance premiums',
    ],
    setupWeeks: '1-2 weeks',
    legalReview: 'Standard',
  },
  {
    id: 'gchra',
    name: 'GCHRA (Group Coverage HRA)',
    section105Type: 'gchra',
    coverageLayer: null, // group health already in place
    section125: true,
    headlineInternal: 'Higher-cap supplement to existing group health',
    headlineEmployer: 'Uncapped reimbursement on top of your group plan',
    whyThisPackage: 'No additional coverage layer needed. Same use case as Excepted Benefit but with no IRS cap and an enrollment requirement (employees must be on the group plan).',
    whyThisPackageEmployer: 'Like the lightweight option, but with no cap on what you can contribute. Employees must be enrolled in your group health plan.',
    components: [
      { layer: 'Section 125', role: 'Pre-tax premium deduction — generates FICA savings', status: 'active', costToEmployer: 'Zero', employeeImpact: 'Take-home pay increase' },
      { layer: 'GCHRA', role: 'Uncapped reimbursement for expenses beyond group plan', status: 'recommended', costToEmployer: 'Employer-set, no cap', employeeImpact: 'Tax-free reimbursement (group plan enrollment required)' },
      { layer: 'Existing Group Health', role: 'Already in place — required for participation', status: 'in_place', costToEmployer: 'Existing', employeeImpact: 'Existing coverage' },
    ],
    estMonthlyEmployerCost: 200,
    employerCostRange: [150, 350],
    bestFor: 'Employers who already offer group health and want uncapped HRA',
    bestForEmployer: 'Companies with group health wanting more flexibility',
    pros: ['No IRS cap on contributions', 'Tax-free for employees', '105(h) testing applies'],
    cons: ['Group health enrollment required to participate', 'Limits eligibility vs Excepted Benefit'],
    complianceNotes: [
      'Employees MUST be enrolled in employer\u2019s group health plan',
      'Section 105(h) non-discrimination testing required',
    ],
    setupWeeks: '2-4 weeks',
    legalReview: 'Standard',
  },
  {
    id: 'integrated_merp',
    name: 'Integrated MERP',
    section105Type: 'integrated_merp',
    coverageLayer: null,
    section125: true,
    headlineInternal: 'Maximum 125 integration with no IRS cap',
    headlineEmployer: 'Flexible healthcare fund with no contribution cap',
    whyThisPackage: 'A traditional Section 105 MERP combined with Section 125 for maximum tax efficiency. No IRS cap. Best when the primary goal is bridging eligibility for the HealthCues 125 program.',
    whyThisPackageEmployer: 'A flexible plan that pairs perfectly with your HealthCues Section 125 program. No cap on what you contribute, and employees get tax-free reimbursement.',
    components: [
      { layer: 'Section 125', role: 'Pre-tax premium deduction — generates FICA savings', status: 'active', costToEmployer: 'Zero', employeeImpact: 'Take-home pay increase' },
      { layer: 'Section 105 MERP', role: 'Tax-free medical expense reimbursement', status: 'recommended', costToEmployer: 'Employer-set, no cap', employeeImpact: 'Tax-free reimbursement for qualified expenses' },
    ],
    estMonthlyEmployerCost: 100,
    employerCostRange: [50, 200],
    bestFor: 'Any employer combining 105 with 125, especially for eligibility bridging',
    bestForEmployer: 'Companies wanting a custom approach combined with HealthCues',
    pros: ['No IRS cap', 'Best 125 integration', 'Bridges eligibility gap'],
    cons: ['Does not satisfy ACA mandate alone (under 50 OK)', 'Section 105(h) testing required', 'No coverage layer included'],
    complianceNotes: [
      'Does not count as MEC — pair with MEC if employer is ALE without group health',
      'Section 105(h) non-discrimination testing required',
    ],
    setupWeeks: '2-4 weeks',
    legalReview: 'Standard',
  },
  {
    id: 'integrated_merp_mec',
    name: 'Integrated MERP + MEC',
    section105Type: 'integrated_merp',
    coverageLayer: 'mec',
    section125: true,
    headlineInternal: 'MERP with required MEC for ALE compliance',
    headlineEmployer: 'Flexible healthcare fund + preventive care coverage',
    whyThisPackage: 'Same MERP structure as Package 6 with a MEC plan added. Strongly recommended for ALEs (50+ FTEs) without group health to avoid the $2,970/employee ACA §4980H(a) penalty.',
    whyThisPackageEmployer: 'A flexible reimbursement plan paired with preventive care coverage — comprehensive and ACA-compliant.',
    components: [
      { layer: 'Section 125', role: 'Pre-tax premium deduction — generates FICA savings', status: 'active', costToEmployer: 'Zero', employeeImpact: 'Take-home pay increase' },
      { layer: 'Section 105 MERP', role: 'Tax-free medical expense reimbursement', status: 'recommended', costToEmployer: 'Employer-set, no cap', employeeImpact: 'Tax-free reimbursement' },
      { layer: 'MEC Plan', role: 'ACA penalty avoidance + preventive care', status: 'required', costToEmployer: '$40-60/employee/month', employeeImpact: 'Preventive care + ACA compliance' },
    ],
    estMonthlyEmployerCost: 175,
    employerCostRange: [125, 250],
    bestFor: 'ALEs (50+ FTEs) without group health, combining 105 with 125',
    bestForEmployer: 'Larger companies wanting comprehensive coverage with HealthCues',
    pros: ['Avoids ACA §4980H(a) penalty', 'No IRS cap on MERP', 'Preventive care included'],
    cons: ['MEC adds monthly cost per employee', '105(h) testing required'],
    complianceNotes: [
      'MEC required for ALEs to avoid $2,970/employee ACA penalty',
      'Section 105(h) non-discrimination testing applies to MERP',
    ],
    setupWeeks: '2-4 weeks',
    legalReview: 'Standard',
  },
  {
    id: 'integrated_merp_healthshare',
    name: 'Integrated MERP + Health Share',
    section105Type: 'integrated_merp',
    coverageLayer: 'health_share',
    section125: true,
    headlineInternal: 'MERP with Health Share for preventive-first design',
    headlineEmployer: 'Innovative healthcare with community-based coverage',
    whyThisPackage: 'Forward-thinking design pairing MERP with a Health Share ministry. Aligns with the IBOS preventive healthcare infrastructure positioning. Strong disclaimers required — Health Share is NOT MEC.',
    whyThisPackageEmployer: 'A modern, preventive-first approach combining tax-free reimbursement with community-based healthcare.',
    components: [
      { layer: 'Section 125', role: 'Pre-tax premium deduction — generates FICA savings', status: 'active', costToEmployer: 'Zero', employeeImpact: 'Take-home pay increase' },
      { layer: 'Section 105 MERP', role: 'Tax-free medical expense reimbursement', status: 'recommended', costToEmployer: 'Employer-set, no cap', employeeImpact: 'Tax-free reimbursement' },
      { layer: 'Health Share', role: 'Community-based preventive care', status: 'alternative', costToEmployer: '$75-150/employee/month', employeeImpact: 'Telemedicine + Rx discounts + community sharing' },
    ],
    estMonthlyEmployerCost: 200,
    employerCostRange: [150, 350],
    bestFor: 'Innovative employers, preventive-first programs',
    bestForEmployer: 'Forward-thinking companies wanting modern healthcare benefits',
    pros: ['Modern, preventive-first', 'No IRS cap on MERP', 'Lower cost than MEC option'],
    cons: ['Health Share ≠ MEC', 'Legal review required', 'May have ACA exposure for ALEs'],
    complianceNotes: [
      'Health Shares are NOT insurance and are NOT MEC',
      'ALEs (50+ FTEs) without group health risk ACA §4980H(a) penalty without MEC',
      'Legal review required — consult benefits counsel',
    ],
    setupWeeks: '4-6 weeks',
    legalReview: 'Required',
    flagged: true,
  },
];



// Seed markdown document templates with {merge_field} placeholders.
// These are intentionally lightweight — in production they'd be proper DOCX/PDF templates.
export const SEED_105_DOCUMENT_TEMPLATES = {
  merp_plan_doc: `# Section 105 Medical Expense Reimbursement Plan

**Plan Sponsor:** {company_name}
**Effective Date:** {effective_date}
**Plan Administrator:** {administrator_name}

## Article I — Purpose

The {company_name} Medical Expense Reimbursement Plan (the "Plan") is established under Section 105(b) of the Internal Revenue Code to reimburse eligible employees on a tax-free basis for qualified medical expenses not covered by other insurance.

## Article II — Eligibility

Eligible employees include: {eligible_classes}

Eligibility begins: {eligibility_waiting_period}

## Article III — Employer Contributions

The Plan Sponsor will contribute **\${contribution_amount}/month** per eligible employee. Contributions are made as reimbursements for substantiated qualified medical expenses.

## Article IV — Qualified Medical Expenses

The Plan reimburses qualified medical expenses as defined in IRC §213(d), including but not limited to:

- Deductibles, copays, and coinsurance
- Prescription drugs
- Preventive care and wellness
- Telemedicine visits
- Dental and vision care

## Article V — Non-Discrimination

This Plan is designed to comply with Section 105(h) non-discrimination requirements. Annual testing will be performed by the Plan Administrator.

## Article VI — Plan Year

The Plan Year begins {plan_year_start} and ends on the last day of the twelfth month following.
`,
  merp_spd: `# {company_name} Healthcare Reimbursement Plan — Summary

Dear Team,

{company_name} is pleased to announce a new benefit: a Section 105 Healthcare Reimbursement Plan, effective {effective_date}.

## What it means for you

You'll receive up to **\${contribution_amount}/month** in tax-free reimbursement for qualified medical expenses like:

- Deductibles and copays
- Prescriptions
- Telemedicine and preventive care
- Dental and vision

## How to use it

1. Pay for a qualified expense
2. Submit a claim form with your receipt
3. Get reimbursed — tax-free

## Questions?

Reach out to {administrator_name} anytime.
`,
  merp_claim: `# Reimbursement Claim Form

**Employee:** ____________________
**Plan Year:** {plan_year_start}
**Date of Service:** ____________
**Provider:** ________________________
**Amount:** \$_________
**Type of Expense:** __________________

**Attach receipt/EOB and submit to {administrator_name}.**

I certify these expenses are qualified medical expenses under IRC §213(d) and have not been reimbursed by any other plan.

Signature: __________________ Date: __________
`,
  merp_checklist: `# Section 105 MERP Compliance Checklist

- [ ] Plan document drafted and adopted by board resolution
- [ ] Summary Plan Description distributed to employees within 90 days of eligibility
- [ ] Section 105(h) non-discrimination test completed annually
- [ ] Form 5500 filed (if participant count triggers requirement)
- [ ] Reimbursement substantiation procedures documented
- [ ] Third-party administrator engaged (if applicable)
- [ ] Plan Year: {plan_year_start}
- [ ] Next testing date: ____________
`,
  merp_talking_points: `# Talking Points — Section 105 MERP

- Employer contributes \${contribution_amount}/month per eligible employee — **fully tax-deductible**
- Employees get **tax-free reimbursement** (no income tax, no FICA)
- **Bridges eligibility gap**: {bridged_count} additional employees qualify for the HealthCues program
- Additional annual FICA savings: **\${bridge_savings}/yr**
- Combined with Section 125: **double-layer tax efficiency**
- Setup time: 2-4 weeks
`,
  qsehra_plan_doc: `# Qualified Small Employer HRA Plan Document

**Plan Sponsor:** {company_name}
**Effective Date:** {effective_date}

## Article I — Purpose

This QSEHRA is established under IRC §9831(d) to reimburse eligible employees for qualified medical expenses and individual health insurance premiums on a tax-free basis.

## Article II — Contribution Limits

For {plan_year_start}:
- Individual: up to \$6,150/year
- Family: up to \$12,450/year

The employer will contribute **\${contribution_amount}/month** per eligible employee (subject to annual IRS caps).

## Article III — Eligibility Notice

A 90-day advance written notice will be provided to all eligible employees before each plan year.

## Article IV — Required Coverage

Eligible employees must maintain Minimum Essential Coverage (MEC) to receive tax-free reimbursements.
`,
  qsehra_notice: `# QSEHRA 90-Day Notice

**To:** All eligible employees of {company_name}
**Effective Date:** {effective_date}

{company_name} will offer a Qualified Small Employer Health Reimbursement Arrangement (QSEHRA) beginning {effective_date}.

- Monthly reimbursement: up to **\${contribution_amount}/month**
- Annual maximums apply (IRS-set caps)
- You must maintain Minimum Essential Coverage (MEC) to receive tax-free reimbursements
- Notify the Marketplace if you receive premium subsidies — QSEHRA amounts offset subsidy eligibility

Questions? Contact {administrator_name}.
`,
  ichra_plan_doc: `# ICHRA Plan Document

**Plan Sponsor:** {company_name}
**Effective Date:** {effective_date}

## Article I — Purpose

This Individual Coverage Health Reimbursement Arrangement is established under 29 CFR §2590.702-2 to reimburse eligible employees for individual health insurance premiums and qualified medical expenses.

## Article II — Employee Classes

{eligible_classes}

## Article III — Contribution Amounts

By class: {class_contributions}

Base contribution: **\${contribution_amount}/month**

## Article IV — Coverage Requirement

Employees must enroll in individual health insurance (including Marketplace or off-Marketplace plans) to receive reimbursements.
`,
  excepted_benefit_plan_doc: `# Excepted Benefit HRA Plan Document

**Plan Sponsor:** {company_name}
**Effective Date:** {effective_date}

## Article I — Purpose

This Excepted Benefit HRA supplements the Plan Sponsor's group health insurance with tax-free reimbursement for copays, deductibles, and other qualified expenses.

## Article II — Annual Cap

Maximum annual reimbursement: **\$2,150** per employee ({plan_year_start} IRS limit).

## Article III — Eligible Expenses

Deductibles, copays, coinsurance, dental, vision, prescriptions, and OTC items — but not individual health insurance premiums.
`,
  gc_plan_doc: `# Group Coverage HRA Plan Document

**Plan Sponsor:** {company_name}
**Effective Date:** {effective_date}

## Article I — Purpose

This Group Coverage HRA supplements the Plan Sponsor's group health insurance with uncapped tax-free reimbursement for qualified medical expenses.

## Article II — Eligibility

Eligibility is conditioned on enrollment in the Plan Sponsor's group health plan.

## Article III — Contribution

The Plan Sponsor will contribute **\${contribution_amount}/month** per eligible employee, subject to Section 105(h) non-discrimination testing.
`,
};
